import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";

interface DayWater {
  day: string;
  actual: number;   // litres consumed
  target: number;   // daily target in litres
}

interface WaterIntakeChartProps {
  data?: DayWater[];
  vsLastWeekPercent?: number;
  avgLiters?: number;
  bare?: boolean;
}


function computeYAxis(data: DayWater[]): { domainMax: number; ticks: number[] } {
  const overallMax = Math.max(...data.map((d) => Math.max(d.actual, d.target)), 0);

  if (overallMax === 0) {
    return { domainMax: 4, ticks: [0, 1, 2, 3, 4] };
  }

  // Round up to nearest whole number so the top tick is always clean
  const domainMax = Math.ceil(overallMax);

  // Use 0.5L steps for small ranges, 1L steps otherwise — fills the axis evenly
  const step = domainMax <= 2 ? 0.5 : 1;
  const ticks: number[] = [];
  for (let v = 0; v <= domainMax + Number.EPSILON; v = parseFloat((v + step).toFixed(10))) {
    ticks.push(parseFloat(v.toFixed(1)));
  }

  return { domainMax, ticks };
}

const getBarColor = (actual: number, target: number): string => {
  if (actual === 0) return "#FF3B30";
  const pct = actual / target;
  if (pct < 0.5) return "#FFB532";
  return "#20BCBB";
};

/**
 * Custom bar shape that renders:
 *  - Gray full-height background (= target)
 *  - Colored foreground (= actual)
 *  - Percentage label
 */
const CustomBar = (props: any) => {
  const {
    x, y, width, height,
    payload,
    background,
  } = props;

  const actual: number = payload?.actual ?? 0;
  const rawTarget: number = payload?.target ?? 0;

  if (width <= 0) return null;

  const domainMax = props.domainMax ?? 4;
  // When API returns no target (0), fall back to domainMax so the gray bar is always visible
  const target = rawTarget > 0 ? rawTarget : domainMax;

  const barColor = getBarColor(actual, target);
  const radius = 3;

  const chartHeightPx: number = background?.height ?? height;
  const chartTopY: number = background?.y ?? y;

  const targetBarHeight = (target / domainMax) * chartHeightPx;
  const targetBarY = chartTopY + chartHeightPx - targetBarHeight;

  const aboveThreshold = domainMax * 0.15;
  const isShortBar = actual < aboveThreshold;

  const label = actual === 0 ? "0L" : `${parseFloat(actual.toFixed(2))}L`;
  const showLabel = actual < rawTarget || rawTarget === 0;
  const labelColor = actual === 0 ? "#FF3B30" : actual < target * 0.5 ? "#1F1F1F" : "#20BCBB";

  return (
    <g>
      {/* Gray background: full target height */}
      <rect
        x={x}
        y={targetBarY}
        width={width}
        height={targetBarHeight}
        fill="#E5E7EB"
        rx={radius}
        ry={radius}
      />

      {/* Colored actual bar */}
      {actual > 0 ? (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={barColor}
          rx={radius}
          ry={radius}
        />
      ) : (
        /* 2 px red stub at the bottom for 0 value */
        <rect
          x={x}
          y={targetBarY + targetBarHeight - 2}
          width={width}
          height={2}
          fill="#FF3B30"
          rx={1}
          ry={1}
        />
      )}

      {/* mL label */}
      {showLabel && (
        isShortBar ? (
          <text
            x={x + width / 2}
            y={actual === 0 ? targetBarY + targetBarHeight - 8 : y - 5}
            textAnchor="middle"
            dominantBaseline="auto"
            fill={labelColor}
            fontSize={9}
            fontFamily="Inter, sans-serif"
            fontWeight={600}
          >
            {label}
          </text>
        ) : (
          <text
            x={x + width / 2}
            y={y + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#1F1F1F"
            fontSize={9}
            fontFamily="Inter, sans-serif"
            fontWeight={600}
          >
            {label}
          </text>
        )
      )}
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const actual = payload[0]?.payload?.actual ?? 0;
  const target = payload[0]?.payload?.target ?? 0;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 6,
        padding: "6px 10px",
        fontSize: 12,
        fontFamily: "Inter, sans-serif",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <p style={{ fontWeight: 600, color: "#1F1F1F", marginBottom: 2 }}>{String(label).slice(0, 2)}</p>
      <p style={{ color: "#20BCBB" }}>Actual: {parseFloat(actual.toFixed(2))}L</p>
      <p style={{ color: "#9CA3AF" }}>Target: {parseFloat(target.toFixed(2))}L</p>
    </div>
  );
};

const WaterIntakeChart: React.FC<WaterIntakeChartProps> = ({
  data = [],
  vsLastWeekPercent,
  avgLiters,
  bare = false,
}) => {
  const isUp = (vsLastWeekPercent ?? 0) >= 0;
  const { domainMax, ticks } = computeYAxis(data);

  const chart = (
    <ResponsiveContainer width="100%" height={140}>
      <BarChart
        data={data}
        barCategoryGap="20%"
        margin={{ top: 20, right: 4, left: -20, bottom: 0 }}
      >
        <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />
        <XAxis
          dataKey="day"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
          dy={6}
          tickFormatter={(v) => String(v).slice(0, 2)}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
          tickFormatter={(v) => `${v}L`}
          domain={[0, domainMax]}
          ticks={ticks}
        />
        <Bar
          dataKey="actual"
          maxBarSize={40}
          minPointSize={2}
          isAnimationActive={true}
          background={{ fill: "transparent" }}
          shape={(shapeProps: any) => (
            <CustomBar {...shapeProps} domainMax={domainMax} />
          )}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  if (bare) return chart;

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Water Intake
        </span>
        {vsLastWeekPercent != null && (
          <div className="flex items-center gap-2">
            <span
              className={`text-[10px] 3xl:text-[12px] font-semibold px-2 py-0.5 rounded-[6px] border font-inter ${
                isUp
                  ? "text-[#20BCBB] border-[#20BCBB] bg-[#F0FAFA]"
                  : "text-[#FF3B30] border-[#FF3B30] bg-[#FFF0F0]"
              }`}
            >
              {isUp ? "+" : ""}{vsLastWeekPercent}%
            </span>
            <span className="text-[10px] 3xl:text-[12px] text-[#8E8E93] font-inter">
              vs last week
            </span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="flex-1">{chart}</div>

      {/* Footer */}
      {avgLiters != null && (
        <div className="text-[12px] 3xl:text-[14px] text-[#8D8D8D] font-inter text-center mt-1">
          Avg liters in this week :{" "}
          <span className="font-semibold text-[#1F1F1F]">{avgLiters}L</span>
        </div>
      )}
    </div>
  );
};

export default WaterIntakeChart;